import { Button, Collapse, CollapseProps, Flex, Skeleton } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

// components
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import ActionListHeaderComponent from 'modules/main/actions/listHeader';
import ListButton from 'components/listButton/listButton';
import BoardItem from '../components/boardItem';
import NoData from 'components/noData/noData';
import Icon from 'utils/icon';

// services
import { onDeleteBoard, onGetBoardsList } from '../services/boardServices';
import NotificationService from 'core/services/notification';

// models
import { Board } from '../models/board';
import { dateFormat } from 'utils/dateFormat';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import InfiniteScroll from 'react-infinite-scroll-component';

// hooks
import useCompanySuspended from 'core/hooks/useCompanySuspended';

// components
import SuspendedTooltip from 'components/suspendedTooltip/suspendedTooltip';

//styles

const panelStyle: React.CSSProperties = {
	padding: '10px 20px',
	marginBottom: 24,
	background: '#fff',
	borderRadius: 'var(--radius)',
	border: 'none',
};

interface BoardsListPageProps {}

const BoardsListPage: FunctionComponent<BoardsListPageProps> = () => {
	const { t } = useTranslation();

	const { suspendedProps } = useCompanySuspended();

	const navigate = useNavigate();
	const location = useLocation();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [items, setItems] = useState<CollapseProps['items']>([]);

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
	const [isDeleting, setIsDeleting] = useState<boolean>(false);
	const [deletedBoardId, setDeletedBoardId] = useState<number | null>(null);

	useEffect(() => {
		getBoards();
	}, []);

	const getBoards = async () => {
		try {
			setIsLoading(true);

			const response = await onGetBoardsList(1);
			setTotalRecords(response.pagination.total);

			setItems(generateBoardsItems(response.boards));

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const fetchMoreData = async () => {
		try {
			const response = await onGetBoardsList(currentPage + 1);

			setItems((prevItems) => [...prevItems!, ...generateBoardsItems(response.boards)!]);

			setCurrentPage(currentPage + 1);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsLoading(false);
		}
	};

	const generateBoardsItems = (boards: Board[]): CollapseProps['items'] => {
		return boards.map((board, index) => ({
			key: board.id,
			style: panelStyle,
			children: <BoardItem board={board} refresh={getBoards} />,
			label: (
				<Flex justify="space-between" align="center">
					<div>
						<h6 className={`mb-0 ${board.status === 'active' ? 'text-primary' : ''}`}>
							{board.status === 'active'
								? t('ACTIONS.ACTIVE_BOARD')
								: board.status === 'draft'
									? `${t('ACTIONS.DRAFT_BOARD')}`
									: `${t('ACTIONS.PREVIOUS_BOARD')}  #${index + 1}`}
						</h6>

						{board.status === 'active' && <p className="mt-2 mb-0 text-light">{dateFormat(board.appointed_at)}</p>}
					</div>

					{board.status === 'draft' && (
						<Flex gap={20} align="center">
							<ListButton
								type="delete"
								{...suspendedProps}
								onClick={(e) => {
									e.stopPropagation();
									setDeletedBoardId(board.id);
									setDeleteModalOpen(true);
								}}
							/>

							<SuspendedTooltip>
								<Button
									type="primary"
									size="middle"
									onClick={(e) => {
										e.stopPropagation();
										navigate(`/dashboard/actions/boards/${board.id}/attach-members`);
									}}>
									{t('COMMON.CONTINUE')}
								</Button>
							</SuspendedTooltip>
						</Flex>
					)}
				</Flex>
			),
		}));
	};

	const deleteHandler = async () => {
		try {
			setIsDeleting(true);

			await onDeleteBoard(deletedBoardId!);

			setIsDeleting(false);
			setDeleteModalOpen(false);
			getBoards();

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.BOARD_DELETED_SUCCESSFULLY'));
		} catch (error) {
			setIsDeleting(false);
			NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.BOARD_DELETED_FAILED'));
		}
	};

	return (
		<>
			<ActionListHeaderComponent title={t('ACTIONS.BOARDS')} subTitle={t('ACTIONS.BOARDS_DESC')} newRequestText={t('ACTIONS.APPOINT_BOARD')} />

			{isLoading ? (
				<LoadingComponent />
			) : items!.length === 0 ? (
				<NoData message=" " />
			) : (
				<InfiniteScroll
					dataLength={items!.length}
					next={fetchMoreData}
					hasMore={totalRecords > items!.length}
					loader={<Skeleton.Button block active style={{ height: 84, borderRadius: 'var(--radius)' }} />}>
					{
						<Collapse
							items={items}
							bordered={false}
							style={{ background: '#f9fafb' }}
							expandIconPosition="end"
							expandIcon={(props) => (
								<Icon
									style={{ transform: props.isActive ? 'rotate(-90deg)' : 'rotate(90deg)', transition: 'transform 0.2s' }}
									name="rightArrow"
									height={16}
									width={16}
									color="var(--primary)"
								/>
							)}
						/>
					}
				</InfiniteScroll>
			)}

			{/* DELETE MODAL */}
			<ConfirmModalComponent
				open={deleteModalOpen}
				title={t('ACTIONS.DELETE_BOARD')}
				subTitle={t('ACTIONS.CONFIRM_DELETE_BOARD')}
				confirmText={t('COMMON.DELETE')}
				noteType="red"
				note={t('ACTIONS.BOARD_DELETE_NOTE')}
				confirmColor="red"
				onDiscard={() => setDeleteModalOpen(false)}
				onConfirm={deleteHandler}
				isLoading={isDeleting}
			/>
		</>
	);
};

export default BoardsListPage;
